import "./css/style.css";
import "./App.css";
import Header from "./common/Header";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import {
  Cart,
  Home,
  Shop,
  ProductItem,
  Login,
  Confirmation,
  AdminDashboard,
} from "./pages";
import Footer from "./common/Footer";
import NotFoundPage from "./pages/NotFoundPage/NotFoundPage";
import { useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { auth } from "./config/firebase";
import checkUserSignedIn from "./util/AuthUtil";
import { useNavigate } from "react-router-dom";
import ProceedToCheckoutView from "./components/ProceedToCheckoutView";
import Orders from "./pages/Orders/Orders";
import BlogsPage from "./pages/BlogsPage/BlogsPage";
import Wholesale from "./pages/Wholesale/Wholesale";
import { Helmet } from "react-helmet";
import Terms from "./pages/Terms/Terms";
import Shipping from "./pages/Shipping/Shipping";
import ReturnRefund from "./pages/ReturnRefund/ReturnRefund";
import Privacy from "./pages/Privacy/Privacy";
import Contact from "./pages/Contact";
import TradeRates from "./pages/TradeRates/TradeRates";
import CategoryPage from "./pages/CategoryPage/CategoryPage";
import { CATEGORY_PAGES } from "./seo/categories";
import HelpWidget from "./components/HelpWidget/HelpWidget";

function AppContent() {
  const { totalItems } = useSelector((state) => state.cart);
  const [authUser, setAuthUser] = useState(null);
  const location = useLocation();

  function openCatalogue() {
    window.open("/assets/docs/NT-Sticks-Catalogue.pdf");
  }

  useEffect(() => {
    checkUserSignedIn(auth, setAuthUser);
  }, [authUser, location.pathname]);

  // Adaptive catalogue button styling based on background
  useEffect(() => {
    const adaptCatalogueButton = () => {
      const catalogueBtn = document.querySelector('.ad_item.floating_cart');
      if (!catalogueBtn) return;

      // Get the background color of the body or main content area
      const body = document.body;
      const computedStyle = window.getComputedStyle(body);
      const backgroundColor = computedStyle.backgroundColor;
      
      // Check if background is dark/brown or light/white
      const isDarkBackground = () => {
        // Check for brown/dark colors in background or if page has brown sections
        const brownSections = document.querySelectorAll('[style*="background"]');
        const hasBrownBg = Array.from(brownSections).some(section => {
          const bgStyle = section.style.background || section.style.backgroundColor;
          return bgStyle && (bgStyle.includes('brown') || bgStyle.includes('#8B4513') || bgStyle.includes('var(--logo-sticks)'));
        });
        
        // Also check current page route for brown backgrounds
        const brownPages = ['/shop', '/cart'];
        
        return hasBrownBg || brownPages.includes(location.pathname) || backgroundColor.includes('brown');
      };

      // Remove existing classes
      catalogueBtn.classList.remove('on-white-bg', 'on-brown-bg');
      
      // Add appropriate class
      if (isDarkBackground()) {
        catalogueBtn.classList.add('on-brown-bg');
      } else {
        catalogueBtn.classList.add('on-white-bg');
      }
    };

    // Run on mount and route changes
    adaptCatalogueButton();
    
    // Listen for route changes
    const handleRouteChange = () => {
      setTimeout(adaptCatalogueButton, 100); // Small delay to let page render
    };
    
    window.addEventListener('popstate', handleRouteChange);
    
    return () => {
      window.removeEventListener('popstate', handleRouteChange);
    };
  }, [location.pathname]);

  return (
    <div>
      <Helmet>
        <title>NT Sticks — Premium Dhoop Sticks, Incense Sticks & Havan Cups | Indore</title>
        <meta
          name="description"
          content="Shop premium dhoop sticks, incense sticks, sambrani cups & havan cups direct from our Indore factory. 100% natural, charcoal-free, low-smoke fragrances."
        />
        <meta
          name="keywords"
          content="dhoop sticks, incense sticks, havan cups, sambrani cups, premium incense, charcoal free dhoop, low smoke incense, wholesale incense, incense manufacturer Indore, NT Sticks, agarbatti"
        />
      </Helmet>
      <Header />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/shop" element={<Shop />} />
        <Route path="/blogs" element={<BlogsPage />} />
        <Route path="/cart" element={<Cart />} />
        <Route path="/login" element={<Login />} />
        <Route path="/confirmation" element={<Confirmation />} />
        <Route path="/item/:id" element={<ProductItem />} />
        <Route path="/wholesale" element={<Wholesale />} />

        {/* Category landing pages. Declared from seo/categories.js rather than
            written out here so a new category is one edit, not two — and so
            the routes can never disagree with the sitemap, which reads the
            same list. Must stay above the "/*" catch-all. */}
        {CATEGORY_PAGES.map((cat) => (
          <Route key={cat.path} path={cat.path} element={<CategoryPage />} />
        ))}

        <Route path="/*" element={<NotFoundPage />} />
        <Route path="/myorders" element={<Orders />} />
        <Route path="/dashboard" element={<AdminDashboard />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/shipping" element={<Shipping />} />
        <Route path="/returnrefund" element={<ReturnRefund />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/contact" element={<Contact />} />
        {/* Unlisted: wholesale rate card. Deliberately absent from the nav, the
            footer, the sitemap and robots.txt allows. Reachable only by URL. */}
        <Route path="/trade-rates" element={<TradeRates />} />
      </Routes>

      <Footer />

      {/* Answers common questions from a fixed knowledge base — no model, no
          network, no running cost, and WhatsApp always one click away inside it.
          Replaced the separate <FloatingWhatsApp> launcher, which sat beside
          this one doing a subset of the same job. */}
      <HelpWidget />

      {/* Browsing aids stay out of the checkout.
          On /cart and /confirmation the customer is filling in a form, and the
          catalogue pill docks bottom-left straight over the delivery fields —
          a PDF download is the last thing wanted mid-order. */}
      {!["/cart", "/confirmation"].includes(location.pathname) && (
      <div className="ad_container ads_position">
        <span style={{padding: '5px !important'}}
          className="btn ad_item floating_cart" 
          onClick={openCatalogue}
          title="Download NT Sticks Product Catalogue (PDF)"
        >
          <i className="fas fa-book-open" style={{marginBottom: '4px', fontSize: '14px'}}></i>
          CATALOGUE
        </span>
      </div>
      )}
    </div>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
